import { useRef } from 'react'

export function DemoProjects() {
  const projects = [
    {
      title: 'Fondriest Center for Environmental Studies',
      description: 'Education and Research Center',
      image: '/images/demo1.jpg'
    },
    {
      title: 'Regional Science Consortium',
      description: 'Water Intelligence at Presqu\'ile',
      image: '/images/demo2.jpg'
    }
  ]

  // Mouse drag-to-scroll. Touch and trackpad already scroll the row natively,
  // so this only handles `pointerType === 'mouse'` — hijacking touch here
  // would fight the browser's own (smoother) momentum scrolling.
  const scrollerRef = useRef(null)
  const drag = useRef({ active: false, startX: 0, startLeft: 0 })

  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse') return
    const el = scrollerRef.current
    if (!el) return
    drag.current = { active: true, startX: e.clientX, startLeft: el.scrollLeft }
    // Pointer capture keeps the drag alive when the cursor leaves the row.
    el.setPointerCapture?.(e.pointerId)
  }
  const onPointerMove = (e) => {
    if (!drag.current.active) return
    const el = scrollerRef.current
    if (!el) return
    el.scrollLeft = drag.current.startLeft - (e.clientX - drag.current.startX)
  }
  const endDrag = (e) => {
    if (!drag.current.active) return
    drag.current.active = false
    // Releasing lets CSS scroll-snap settle the row on the nearest card.
    scrollerRef.current?.releasePointerCapture?.(e.pointerId)
  }

  return (
    <section id="projects" className="py-24 bg-white">
      <div className="max-w-[1920px] mx-auto px-8 sm:px-6 lg:px-[140px]">
        <h2 className="common-head28 text-3xl font-bold text-[#4D4D4D] mb-7">Demo Projects</h2>

        {/* One row, two behaviours — deliberately a single set of card markup
            rather than a mobile copy and a desktop copy, so the two can't
            drift apart.

            < 768px: a snap-scrolling swipe row. Cards are 85% wide so the
              next one peeks in at the edge, exactly as in the XD frame,
              which is what tells the user there is more to swipe to.
              The bleed is RIGHT-ONLY (`-mr-8`, no negative left margin), so
              the first card keeps the section's left inset and lines up with
              the "Demo Projects" heading, as in the XD. A symmetric `-mx-8`
              pulled the first card flush against the screen edge and lost
              that alignment. `pr-8` gives the row trailing scroll space so
              the last card isn't jammed against the edge at the end.

            >= 768px: `md:` classes restore the original
              `grid grid-cols-2 gap-12` layout — unchanged. */}
        <div
          ref={scrollerRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="swipe-row flex gap-6 overflow-x-auto snap-x snap-mandatory -mr-8 pr-8 pb-2
                     cursor-grab active:cursor-grabbing select-none
                     md:mr-0 md:pr-0 md:pb-0 md:gap-12 md:grid md:grid-cols-2 md:overflow-visible
                     md:cursor-auto md:select-auto"
        >
          {projects.map((project, idx) => (
            <div
              key={idx}
              className="group w-[85%] shrink-0 snap-start rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition duration-300
                         md:w-auto md:shrink"
            >
              {/* Project Image */}
              <div className="relative overflow-hidden text-[#4D4D4D] height-400">
                <img
                  src={project.image}
                  alt={project.title}
                  // Without this, dragging the row by an image starts the
                  // browser's native image-drag instead of scrolling.
                  draggable={false}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                />
              </div>

              {/* Project Info */}
              <div className="bg-white p-6 md:p-8">
                <h3 className="common-head25 text-xl font-bold text-[#4D4D4D] mb-1 md:mb-3">
                  {project.title}
                </h3>
                <p className="text-xl text-gray-600">
                  {project.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
